/**
 * The Job catalog.
 *
 * These are default job grants. Project-specific individual tool choices
 * override them. Effective permission is the UNION of explicitly assigned jobs,
 * intersected with the workspace sandbox. This is enforced inside the worker
 * process at call time -- never by asking the model nicely in a prompt.
 *
 * By default the two board-write jobs (Idea Creator, Orchestrator) have
 * NO filesystem access, and the two file-heavy jobs deliberately have NO board
 * access. Keeping those disjoint is what stops an autonomous agent from quietly
 * expanding its own remit.
 */

export const TOOL_GROUPS = [
  // Loop control. Compaction lives here so every member can shorten its own
  // context, regardless of its jobs.
  'control',
  'fs.read',
  'fs.write',
  'fs.edit',
  'fs.delete',
  'shell',
  'git',
  'web.search',
  'web.fetch',
  'board.write',
  'board.assign',
  'engine',
] as const;

export type ToolGroup = (typeof TOOL_GROUPS)[number];

export const JOB_IDS = [
  'heavy_coder',
  'light_coder',
  'websearch_agent',
  'idea_creator',
  'orchestrator',
  'modeler',
  'level_designer',
  'gameplay_programmer',
] as const;

export type JobId = (typeof JOB_IDS)[number];

/** True when `jobs` includes every catalog job. */
export function holdsEveryJob(jobs: readonly string[]): boolean {
  return JOB_IDS.every((id) => jobs.includes(id));
}

export interface JobSpec {
  readonly id: JobId;
  readonly label: string;
  readonly summary: string;
  readonly tools: readonly ToolGroup[];
  /** May start work with no task assigned. Only board-write jobs qualify. */
  readonly selfInitiating: boolean;
  /** May create/modify tasks on the board. */
  readonly boardWrite: boolean;
  /** May assign tasks to other members. */
  readonly canAssign: boolean;
  /** Appended under the immutable platform rules. Specialisation only. */
  readonly guidance: string;
}

export const JOBS: Record<JobId, JobSpec> = {
  modeler: {
    id: 'modeler',
    label: 'Modeler',
    summary: 'Create and refine Prism Engine Cloud models and materials.',
    tools: ['fs.read', 'fs.write', 'fs.edit', 'engine'],
    selfInitiating: false,
    boardWrite: false,
    canAssign: false,
    guidance:
      'Read AGENTS.md, including the Modeling guide. Work in passes: plan real-world size and big forms, block out, then prism_model_render the 6-view sheet and fix silhouette and proportions before detailing; add secondary forms and details with prism_model_edit; after each pass critique the render (gaps, floating parts, scale, faceting, flat surfaces) and iterate until nothing is left to fix. Never finish after a single render. Characters, animals and creatures are one `sculpt` part (smooth-blended anatomy), never stacks of capsules; for people start from prism_model_template "human". For complex subjects, make a concept picture with prism_image_generate first and model from it. Clothes are `sewn` garments on the body part of the character (see the manual); fabric looks come from fabric materials, with prism_image_generate for printed patterns.',
  },
  level_designer: {
    id: 'level_designer',
    label: 'Level Designer',
    summary: 'Build scenes, terrain, lighting and world layouts.',
    tools: ['fs.read', 'fs.write', 'fs.edit', 'engine'],
    selfInitiating: false,
    boardWrite: false,
    canAssign: false,
    guidance:
      'Read AGENTS.md. Edit Prism Engine Cloud scene documents and inspect prism_screenshot before finishing.',
  },
  gameplay_programmer: {
    id: 'gameplay_programmer',
    label: 'Gameplay Programmer',
    summary: 'Implement Rhai gameplay, prefabs and game data.',
    tools: ['fs.read', 'fs.write', 'fs.edit', 'shell', 'engine'],
    selfInitiating: false,
    boardWrite: false,
    canAssign: false,
    guidance:
      'Read AGENTS.md. Use the documented Rhai API, prism_check and Play mode to verify gameplay.',
  },
  heavy_coder: {
    id: 'heavy_coder',
    label: 'Heavy Coder',
    summary: 'Structural work: new modules, rewrites, refactors, migrations.',
    tools: [
      'fs.read',
      'fs.write',
      'fs.edit',
      'fs.delete',
      'shell',
      'git',
      'web.search',
      'web.fetch',
    ],
    selfInitiating: false,
    boardWrite: false,
    canAssign: false,
    guidance: `You implement structural work: new modules, rewrites, refactors, migrations, cross-cutting changes.

Read the surrounding code before you change it, and match the conventions already in the file.
Destructive operations (delete, move) and shell commands require approval when outside the
workspace root -- that is expected, not an error. Prefer small, reversible steps.`,
  },

  light_coder: {
    id: 'light_coder',
    label: 'Light Coder',
    summary: 'Contained edits: fixes, small features, copy and config tweaks.',
    tools: ['fs.read', 'fs.edit', 'web.search', 'web.fetch'],
    selfInitiating: false,
    boardWrite: false,
    canAssign: false,
    guidance: `You make contained edits: bug fixes, small features, copy changes, config tweaks.

You cannot create, delete or move files and cannot run shell commands. If a task needs any of
those, stop and report that it needs a Heavy Coder instead of attempting a workaround.`,
  },

  websearch_agent: {
    id: 'websearch_agent',
    label: 'WebSearch Agent',
    summary: 'Research: searches the web and reads fetched pages.',
    tools: ['web.search', 'web.fetch'],
    selfInitiating: false,
    boardWrite: false,
    canAssign: false,
    guidance: `You research questions using web search and page fetches.

Cite the URL for every non-obvious claim. Distinguish what a source actually says from your own
inference. Never fetch a URL you were not asked about.`,
  },

  idea_creator: {
    id: 'idea_creator',
    label: 'Idea Creator',
    summary: 'Proposes new tasks for the board. No filesystem access.',
    tools: ['board.write'],
    selfInitiating: true,
    boardWrite: true,
    canAssign: false,
    guidance: `You propose work. You have no filesystem access at all -- you do not read or write code.

Each idea you file must have a unique kebab-case task name, a single clear job, a priority from
1-100, and a description that states the outcome rather than the steps. Do not file speculative
chores. If the board already has an open task covering the same ground, extend that one instead.`,
  },

  orchestrator: {
    id: 'orchestrator',
    label: 'Orchestrator',
    summary: 'Delegates queued tasks to members by priority and job. No filesystem access.',
    tools: ['board.write', 'board.assign'],
    selfInitiating: true,
    boardWrite: true,
    canAssign: true,
    guidance: `You delegate work. You have no filesystem access at all -- you do not read or write code
and you cannot run commands.

Assign the highest-priority ready task to an active member whose jobs match that task's job and who
is not already busy. If no member matches, say so rather than assigning to a poor fit. Never assign
a task to a member that is switched off or missing the required job.`,
  },
};

export function isJobId(value: unknown): value is JobId {
  return typeof value === 'string' && (JOB_IDS as readonly string[]).includes(value);
}

/**
 * Accept a job id, its label, or the same words with spaces or hyphens.
 * "Heavy Coder" and "heavy-coder" are `heavy_coder`. "coder" is not a job.
 */
export function resolveJobId(value: string): JobId | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (isJobId(trimmed)) return trimmed;
  const folded = trimmed.toLowerCase().replace(/[\s-]+/g, '_');
  if (isJobId(folded)) return folded;
  const label = trimmed.toLowerCase();
  return JOB_IDS.find((id) => JOBS[id].label.toLowerCase() === label) ?? null;
}

/** Exact ids with their labels, for tool errors and descriptions. */
export function formatJobChoices(): string {
  return JOB_IDS.map((id) => `${id} (${JOBS[id].label})`).join(', ');
}

/**
 * Union of the tool groups granted by every listed job.
 *
 * `control` is always included: it carries compaction, and a member without it
 * could grow a conversation it has no legal way to shorten.
 */
export function toolsForJobs(jobs: readonly JobId[]): ToolGroup[] {
  const out = new Set<ToolGroup>(['control']);
  if (jobs.length) out.add('engine');
  for (const job of jobs) {
    for (const tool of JOBS[job].tools) out.add(tool);
  }
  return [...out];
}

export function jobLabel(id: JobId): string {
  return JOBS[id].label;
}

/**
 * Jobs a member with the given assignment may run autonomously.
 * Non-self-initiating jobs only run when a task is assigned.
 */
export function selfInitiatingJobs(jobs: readonly JobId[]): JobId[] {
  return jobs.filter((j) => JOBS[j].selfInitiating);
}

/** True when this job assignment set may create or modify board tasks. */
export function canWriteBoard(jobs: readonly JobId[]): boolean {
  return jobs.some((j) => JOBS[j].boardWrite);
}

/** True when this job assignment set may assign tasks to other members. */
export function canAssign(jobs: readonly JobId[]): boolean {
  return jobs.some((j) => JOBS[j].canAssign);
}
